/**
 * User tab routes.
 *
 * ⚠️ KIR-2659 deep-link contract: the `Alerts: 'alerts'` string value
 * is also baked into the desktop agent's and IDE extensions'
 * summary-banner URL builders (`buildSummaryDeepLinkURL` in
 * extensions/agent/pkg/notifications/coordinator.go and
 * extensions/vscode-core/src/services/notifications/coordinator.ts)
 * as the second path segment after `/dashboard/`. Renaming the
 * `'alerts'` literal silently breaks every notification deep-link to
 * the personal alerts view — update the agent + extension URL
 * builders in the same PR.
 */
export const UserTab = {
  Policies: 'policies',
  // KIR-3816: top-level Secure AI Coding Enforcement page (between Policies and
  // Inventory). Gated by the ai_instruction_catalog flag. KIR-3925 groups it
  // under the "AI AppSec" sidebar section together with the two tabs below.
  SecureAiCoding: 'secure-ai-coding-enforcement',
  // KIR-3925: AI AppSec pages, gated by the ai_instruction_catalog flag.
  ThreatModeling: 'threat-modeling',
  LlmVulnDiscovery: 'llm-vulnerability-discovery',
  Alerts: 'alerts',
  PostureManagement: 'inventory',
  ShadowAI: 'shadow-ai',
  Settings: 'settings',
  Install: 'install',
  AttackDemonstrations: 'attack-demonstrations',
  AgentsActivity: 'agent-activity',
} as const;

export type UserTabType = (typeof UserTab)[keyof typeof UserTab];

/**
 * Get subheader text for a dashboard tab
 */
export function getSubheaderForTab(tab: UserTabType): string {
  switch (tab) {
    case UserTab.Policies:
      return 'Review your workspace policies';
    case UserTab.SecureAiCoding:
      return 'Enforce secure coding guidance for AI coding agents';
    case UserTab.ThreatModeling:
      return 'Make AI coding agents model threats before writing code';
    case UserTab.LlmVulnDiscovery:
      return 'Find real, exploitable vulnerabilities with OpenAnt';
    case UserTab.ShadowAI:
      return 'View AI tools detected across your devices';
    case UserTab.Settings:
      return 'Account, integrations, notifications, and preferences';
    case UserTab.Install:
      return 'Review and complete the required steps for setup';
    case UserTab.AttackDemonstrations:
      return "Review and test Kirin's capabilities by simulation";
    case UserTab.Alerts:
      return 'Review security alerts from your scans';
    case UserTab.PostureManagement:
      return 'Review and manage your inventory with centralized visibility';
    case UserTab.AgentsActivity:
      return 'Packages installed and repositories accessed by your coding agents';
    default:
      return '';
  }
}
